import React, { useState, useEffect } from "react";
import { getFirestore, doc, deleteDoc, onSnapshot } from "firebase/firestore";
import Swal from "sweetalert2";
import DetallesView from "./DetallesView";
import FacturasView from "./FacturasView";
import GastosView from "./GastosView";
import HistorialView from "./HistorialView";
import InvoiceSearchModal from "./InvoiceSearchModal";

export default function PreviaPedidos({
  orden,
  numeroOrden,
  clientes = [],
  cerrarPrevia,
  onEditar,
  balance = 0,
  ingresos = 0,
  gastosTotales = 0,
  gastosPorCategoria = [],
  ultimasTransacciones = [],
  estado,
}) {
  // Solo manejamos las tabs inferiores
  const [subTab, setSubTab] = useState("facturas");
  const [historyEvents, setHistoryEvents] = useState([]);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  // Setter para GastosView
  const [, setShowGastosList] = useState(false);

  const handleDelete = async () => {
    const res = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará la orden.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (res.isConfirmed) {
      const db = getFirestore();
      await deleteDoc(doc(db, "pedidos", orden.id));
      Swal.fire("Eliminada", "Orden eliminada con éxito", "success").then(() =>
        window.location.reload()
      );
    }
  };

  useEffect(() => {
    const db = getFirestore();
    const ref = doc(db, "pedidos", orden.id);
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() || {};

      if (Array.isArray(data.actionLog)) {
        const normalized = data.actionLog.map((evt) => {
          const ts =
            typeof evt.timestamp === "object" &&
            typeof evt.timestamp.toDate === "function"
              ? evt.timestamp.toDate().toISOString()
              : evt.timestamp;

          return {
            id: evt.id || `${orden.id}_${ts}`,
            ...evt,
            timestamp: ts,
          };
        });
        setHistoryEvents(normalized);
      } else {
        setHistoryEvents([]);
      }
    });

    return () => unsub();
  }, [orden.id]);

  // Estilos mínimos para las tabs inferiores (autocontenidas)
  const TabsStyle = () => (
    <style>{`
      .order-tabs--bottom{
        display:flex; align-items:center; gap:8px;
        border-bottom:1px solid #E6EDFF; padding-bottom:8px;
      }
      .tab-button{
        display:inline-flex; align-items:center; gap:6px;
        padding:8px 12px; border:1px solid #E6EDFF; border-radius:10px;
        background:#fff; color:#111827; cursor:pointer;
      }
      .tab-button.active{ background:#f4f8ff; border-color:#cfe1ff; font-weight:700; }
    `}</style>
  );

  // Subtabs + contenido para el pie de DetallesView
  const footer = (
    <>
      <TabsStyle />
      <div className="order-tabs--bottom" style={{ marginTop: 8 }}>
        {[
          { key: "facturas", icon: "receipt", label: "Facturas" },
          { key: "gastos", icon: "cash-stack", label: "Gastos" },
          { key: "historial", icon: "clock-history", label: "Historial" },
        ].map(({ key, icon, label }) => (
          <button
            key={key}
            className={`tab-button ${subTab === key ? "active" : ""}`}
            onClick={() => setSubTab(key)}
          >
            <i className={`bi bi-${icon}`} />
            <span style={{ marginLeft: 5 }}>{label}</span>
          </button>
        ))}
      </div>

      <div style={{ marginTop: 8 }}>
        {subTab === "facturas" && <FacturasView pedidoId={orden.id} />}

        {subTab === "gastos" && (
          <GastosView
            ordenId={orden.id}
            balance={balance}
            ingresos={ingresos}
            gastosTotales={gastosTotales}
            gastosPorCategoria={gastosPorCategoria}
            ultimasTransacciones={ultimasTransacciones}
            onToggleList={setShowGastosList}
          />
        )}

        {subTab === "historial" && (
          <HistorialView historyEvents={historyEvents} />
        )}
      </div>
    </>
  );

  return (
    <div className="container-gastos">
      <div className="modal-close" onClick={cerrarPrevia}>
        <i className="bi bi-x-lg" />
      </div>

      {/* Encabezado limpio (sin tabs arriba) */}
      <div className="cotizacion-header" style={{ position: "relative" }}>
        <div
          className="header-text"
          style={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <h2>Orden de Compra</h2>
          <h3>#{String(numeroOrden).padStart(4, "0")}</h3>
        </div>
      </div>

      {/* Detalles completo, y al final inyectamos las subtabs */}
      <DetallesView
        /* básicos */
        fechaOrden={orden.fechaOrden}
        clienteId={orden.cliente}
        clientes={clientes}
        usuario={orden.usuario}
        productos={orden.productosSeleccionados}
        descuento={orden.descuento}
        estado={estado ?? orden.estado}
        /* identificadores y valores por defecto */
        pedidoNumero={numeroOrden}
        numeroOrden={numeroOrden}
        ordenId={orden.id}
        folio={orden.folio}
        divisa={orden.divisa || "MXN"}
        ivaPct={typeof orden.ivaPct === "number" ? orden.ivaPct : 16}
        /* envío */
        envio={orden.envio || {}}
        /* campos extra */
        area={orden.area}
        asunto={orden.asunto}
        atencion={orden.atencion}
        correo={orden.correo || orden.email}
        planta={orden.planta}
        division={orden.division}
        /* acciones */
        onEditar={() => onEditar(orden)}
        onImprimir={() => window.print()}
        onFacturar={() => setIsInvoiceModalOpen(true)} // “Asociar factura”
        onEliminar={handleDelete}
        footer={footer}
      />

      <InvoiceSearchModal
        isOpen={isInvoiceModalOpen}
        onClose={() => setIsInvoiceModalOpen(false)}
        pedidoId={orden.id}
      />
    </div>
  );
}
